import { getApiOrigin } from '@/lib/api';

/**
 * Resolves upload URL from various input formats to canonical URL
 * 
 * Input formats supported:
 * - Absolute URL: "https://api-sys.isofh.vn/api/uploads/file/<token>/<filename>" => return nguyên bản
 * - Relative URL: "/api/uploads/file/<token>/<filename>" => prefix với API_ORIGIN
 * - Object: { token: "...", filename: "..." } => build canonical URL
 * - Legacy base64/data URL: "data:image/..." or raw base64 (will trigger migration warning)
 * 
 * Output: Always canonical absolute URL (https://api-sys.isofh.vn/api/uploads/file/<token>/<filename>)
 */
export const resolveUploadUrl = (
  input: string | { token: string; filename: string } | null | undefined
): string | null => {
  if (!input) {
    return null;
  }

  // Object format: { token, filename }
  if (typeof input === 'object' && 'token' in input && 'filename' in input) {
    const API_ORIGIN = getApiOrigin();
    return `${API_ORIGIN}/api/uploads/file/${input.token}/${input.filename}`;
  }

  // String format
  if (typeof input !== 'string') {
    return null;
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  // (1) Ưu tiên cao nhất: Absolute URL (http/https) => return nguyên bản 100%
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    // Normalize double /api/api/uploads/file/ về /api/uploads/file/
    const normalized = trimmed.replace(/\/api\/api\/uploads\/file\//g, '/api/uploads/file/');
    
    if (import.meta.env.DEV) {
      if (normalized !== trimmed) {
        console.log('[resolveUploadUrl] Normalized double /api/api:', {
          original: trimmed.substring(0, 80),
          normalized: normalized.substring(0, 80),
        });
      }
    }
    
    return normalized;
  }

  // (2) Normalize double /api/api/uploads/file/ về /api/uploads/file/
  let normalizedInput = trimmed;
  if (trimmed.includes('/api/api/uploads/file/')) {
    normalizedInput = trimmed.replace(/\/api\/api\/uploads\/file\//g, '/api/uploads/file/');
    if (import.meta.env.DEV) {
      console.log('[resolveUploadUrl] Normalized double /api/api:', {
        original: trimmed.substring(0, 80),
        normalized: normalizedInput.substring(0, 80),
      });
    }
  }

  // (3) Relative path /api/uploads/file/ => prefix với API_ORIGIN (không có /api)
  if (normalizedInput.startsWith('/api/uploads/file/')) {
    const API_ORIGIN = getApiOrigin();
    const resolved = `${API_ORIGIN}${normalizedInput}`;
    
    if (import.meta.env.DEV) {
      console.log('[resolveUploadUrl] Resolved relative upload URL:', {
        input: normalizedInput.substring(0, 60),
        resolved: resolved.substring(0, 80),
      });
    }
    
    return resolved;
  }

  // Data URL or base64 - should not be used, log warning
  if (normalizedInput.startsWith('data:')) {
    if (import.meta.env.DEV) {
      console.warn('[resolveUploadUrl] Data URL detected (should be migrated):', {
        input: normalizedInput.substring(0, 50) + '...',
        suggestion: 'Upload via API and use token+filename format',
      });
    }
    return null;
  }

  // Check if looks like base64 (raw base64 string)
  const base64Pattern = /^[A-Za-z0-9+/=]+$/;
  const looksLikeBase64 = 
    !normalizedInput.startsWith('http') &&
    !normalizedInput.startsWith('//') &&
    !normalizedInput.startsWith('/') &&
    base64Pattern.test(normalizedInput) &&
    (normalizedInput.length > 20 || normalizedInput.endsWith('='));

  if (looksLikeBase64) {
    if (import.meta.env.DEV) {
      console.warn('[resolveUploadUrl] Base64 string detected (should be migrated):', {
        input: normalizedInput.substring(0, 20) + '...',
        suggestion: 'Upload via API and use token+filename format',
      });
    }
    return null;
  }

  // Legacy avatar path: /uploads/avatars/... => map sang /api/uploads/avatars/...
  if (normalizedInput.startsWith('/uploads/avatars/')) {
    const API_ORIGIN = getApiOrigin();
    const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api';
    // Map /uploads/avatars/... => /api/uploads/avatars/...
    const legacyPath = normalizedInput.replace('/uploads/avatars/', '/api/uploads/avatars/');
    const resolved = `${API_ORIGIN}${legacyPath}`;
    
    if (import.meta.env.DEV) {
      console.log('[resolveUploadUrl] Resolved legacy avatar path:', {
        input: normalizedInput.substring(0, 60),
        resolved: resolved.substring(0, 80),
      });
    }
    
    return resolved;
  }

  // Relative path starting with / (không phải /api/uploads/file/ hoặc /uploads/avatars/)
  if (normalizedInput.startsWith('/')) {
    const API_ORIGIN = getApiOrigin();
    return `${API_ORIGIN}${normalizedInput}`;
  }

  // Unknown format - return null
  if (import.meta.env.DEV) {
    console.warn('[resolveUploadUrl] Unknown format:', {
      input: normalizedInput.substring(0, 50),
    });
  }
  return null;
};

